import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { subscribeBatchUploadProgress } from "../services/batchSocket";

const BatchUploadProgressContext = createContext(null);

const DEFAULT_API_BASE_URL =
  import.meta.env.VITE_BASE_URL_DEVELOPMENT || "http://localhost:8000";

const POLL_INTERVAL_MS = 4000;
const LOG = "[CardUpload:Progress]";

function normalizeProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

export function BatchUploadProgressProvider({
  children,
  apiBaseUrl = DEFAULT_API_BASE_URL,
}) {
  const [jobs, setJobs] = useState([]);
  const jobsRef = useRef([]);
  jobsRef.current = jobs;
  const subscriptionsRef = useRef({});
  const pollTimersRef = useRef({});
  const refreshedJobsRef = useRef(new Set());
  const notifiedJobsRef = useRef(new Set());

  const notifyUploadComplete = useCallback((jobId) => {
    if (notifiedJobsRef.current.has(jobId)) return;
    notifiedJobsRef.current.add(jobId);
    const job = jobsRef.current.find((j) => j.id === jobId);
    const batchName = job?.batchName || "Batch";
    toast.success(`"${batchName}" uploaded successfully`);
  }, []);

  const refreshBatchesList = useCallback(async () => {
    const token = localStorage.getItem("access");
    if (!token) return;

    try {
      await fetch(`${apiBaseUrl}/api/v1/cards/batches/?page_size=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.dispatchEvent(new CustomEvent("batches-list-refresh"));
    } catch (err) {
      console.warn(LOG, "refreshBatchesList failed", err);
    }
  }, [apiBaseUrl]);

  const handleUploadSuccess = useCallback(
    (jobId) => {
      if (refreshedJobsRef.current.has(jobId)) return;
      refreshedJobsRef.current.add(jobId);
      refreshBatchesList();
    },
    [refreshBatchesList]
  );

  const patchJob = useCallback((jobId, patch) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === jobId ? { ...job, ...patch } : job))
    );
  }, []);

  const stopPolling = useCallback((jobId) => {
    const timer = pollTimersRef.current[jobId];
    if (timer) {
      clearInterval(timer);
      delete pollTimersRef.current[jobId];
    }
  }, []);

  const cleanupSubscription = useCallback(
    (jobId) => {
      stopPolling(jobId);
      const unsub = subscriptionsRef.current[jobId];
      if (unsub) {
        unsub();
        delete subscriptionsRef.current[jobId];
      }
    },
    [stopPolling]
  );

  const startChunkedUpload = useCallback(
    ({ uploadId, batchName, totalChunks, fileCount }) => {
      setJobs((prev) => {
        if (prev.some((j) => j.uploadId === uploadId)) return prev;
        return [
          ...prev,
          {
            id: uploadId,
            uploadId,
            batchId: null,
            taskId: null,
            batchName: batchName || "Default Batch",
            phase: "chunks",
            status: "pending",
            progress: 0,
            message: null,
            chunkDone: 0,
            chunkTotal: totalChunks,
            fileCount: fileCount || 0,
            error: null,
            minimized: false,
          },
        ];
      });
    },
    []
  );

  const updateChunkProgress = useCallback(
    (uploadId, chunkDone, chunkTotal, serverProgress) => {
      const calculated =
        chunkTotal > 0 ? (chunkDone / chunkTotal) * 50 : 0;
      const progress =
        serverProgress != null
          ? normalizeProgress(serverProgress)
          : normalizeProgress(calculated);

      setJobs((prev) =>
        prev.map((job) =>
          job.uploadId === uploadId && job.phase === "chunks"
            ? { ...job, chunkDone, chunkTotal, progress, status: "pending" }
            : job
        )
      );
    },
    []
  );

  const pollUploadStatus = useCallback(
    async (jobId, taskId) => {
      const token = localStorage.getItem("access");
      if (!token || !taskId) return;

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/v1/cards/upload-status/${taskId}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (response.status === 500 || data?.status === "failed") {
          patchJob(jobId, {
            status: "failed",
            error:
              (typeof data?.result === "string" ? data.result : null) ||
              data?.message ||
              data?.error ||
              "Upload processing failed",
            phase: "processing",
          });
          cleanupSubscription(jobId);
          return;
        }

        if (!response.ok) return;

        if (data?.status === "done") {
          patchJob(jobId, {
            status: "completed",
            progress: 100,
            phase: "processing",
            batchId: data?.result?.batch_id
              ? String(data.result.batch_id)
              : undefined,
          });
          handleUploadSuccess(jobId);
          notifyUploadComplete(jobId);
          cleanupSubscription(jobId);
          return;
        }

        if (data?.status === "queued") {
          patchJob(jobId, { phase: "processing" });
        }
      } catch (err) {
        console.warn(LOG, "pollUploadStatus error", { jobId, taskId, err });
      }
    },
    [apiBaseUrl, cleanupSubscription, handleUploadSuccess, notifyUploadComplete, patchJob]
  );

  const startPolling = useCallback(
    (jobId, taskId) => {
      if (!taskId) return;
      stopPolling(jobId);
      pollUploadStatus(jobId, taskId);
      pollTimersRef.current[jobId] = setInterval(() => {
        pollUploadStatus(jobId, taskId);
      }, POLL_INTERVAL_MS);
    },
    [pollUploadStatus, stopPolling]
  );

  const startBatchProcessing = useCallback(
    ({ uploadId, batchId, taskId, batchName, status, progress }) => {
      const batchIdStr = String(batchId);
      const initialProgress = normalizeProgress(progress ?? 50);

      setJobs((prev) =>
        prev.map((job) => {
          if (job.uploadId !== uploadId) return job;
          return {
            ...job,
            id: batchIdStr,
            batchId: batchIdStr,
            taskId: taskId || null,
            batchName: batchName || job.batchName,
            phase: "processing",
            status: status || "processing",
            progress: Math.max(initialProgress, job.progress),
          };
        })
      );

      cleanupSubscription(batchIdStr);

      const applyProgress = (payload) => {
        patchJob(batchIdStr, {
          status: payload?.status || "processing",
          progress: normalizeProgress(payload?.progress),
          message: payload?.message || null,
          phase: "processing",
        });
      };

      const unsub = subscribeBatchUploadProgress(
        batchId,
        {
          onProgress: applyProgress,
          onComplete: (payload) => {
            patchJob(batchIdStr, {
              status: "completed",
              progress: 100,
              message: payload?.message || null,
              phase: "processing",
            });
            handleUploadSuccess(batchIdStr);
            notifyUploadComplete(batchIdStr);
            cleanupSubscription(batchIdStr);
          },
          onFailed: (payload) => {
            patchJob(batchIdStr, {
              status: "failed",
              error:
                payload?.message ||
                payload?.error ||
                "Batch processing failed",
              phase: "processing",
            });
            cleanupSubscription(batchIdStr);
          },
        },
        apiBaseUrl
      );

      subscriptionsRef.current[batchIdStr] = unsub;

      if (taskId) {
        startPolling(batchIdStr, taskId);
      }
    },
    [apiBaseUrl, cleanupSubscription, handleUploadSuccess, notifyUploadComplete, patchJob, startPolling]
  );

  const setJobMinimized = useCallback(
    (jobId, minimized) => {
      patchJob(jobId, { minimized });
    },
    [patchJob]
  );

  const dismissJob = useCallback(
    (jobId) => {
      refreshedJobsRef.current.delete(jobId);
      notifiedJobsRef.current.delete(jobId);
      cleanupSubscription(jobId);
      setJobs((prev) => prev.filter((job) => job.id !== jobId));
    },
    [cleanupSubscription]
  );

  const value = useMemo(
    () => ({
      jobs,
      startChunkedUpload,
      updateChunkProgress,
      startBatchProcessing,
      setJobMinimized,
      dismissJob,
    }),
    [
      jobs,
      startChunkedUpload,
      updateChunkProgress,
      startBatchProcessing,
      setJobMinimized,
      dismissJob,
    ]
  );

  return (
    <BatchUploadProgressContext.Provider value={value}>
      {children}
    </BatchUploadProgressContext.Provider>
  );
}

export function useBatchUploadProgress() {
  const context = useContext(BatchUploadProgressContext);
  if (!context) {
    throw new Error(
      "useBatchUploadProgress must be used within BatchUploadProgressProvider"
    );
  }
  return context;
}
